import type { ConsentKitConfig, Dictionary, Regime } from "../types";
import { h, on } from "./dom";

/**
 * Deliberately regime-neutral. What "affirm" and "decline" mean differs — accept-all vs.
 * acknowledge, reject-all vs. opt-out — and that mapping belongs to the caller that knows
 * the decision model, not to the layer that draws two buttons.
 */
export interface BannerCallbacks {
  onAffirm(): void;
  onDecline(): void;
  onCustomize(): void;
}

export interface BannerOptions {
  config: ConsentKitConfig;
  t: Dictionary;
  regime: Regime;
  /** A Global Privacy Control signal was honoured before the visitor saw this. */
  gpc: boolean;
  /** Set when any enabled vendor sends data outside the EU — must be said on layer one. */
  hasThirdCountryTransfer: boolean;
}

/**
 * The first layer, in one of two shapes.
 *
 * Under the DSGVO it is a request: who the controller is, what the options are, that data
 * leaves the EU if it does, and both "accept all" and "reject all" as equally weighted
 * buttons. Nothing may be hidden one layer deeper.
 *
 * Under US law it is a notice: the processing is already running, so the layer states that
 * plainly and puts the statutory opt-out on the same footing as the acknowledgement. The
 * equal-weight rule is kept either way — it costs nothing and a dark pattern is a dark
 * pattern under both bodies of law.
 */
export function createBanner(options: BannerOptions, callbacks: BannerCallbacks): HTMLElement {
  const { config, t, regime } = options;
  const optOut = regime === "opt-out";

  const panel = h("div", {
    class: panelClasses(config),
    role: "dialog",
    "aria-modal": "false",
    "aria-labelledby": "ck-banner-title",
    "aria-describedby": "ck-banner-body",
  });

  // Positive action first in the pair, refusal beside it at identical weight.
  const affirm = h("button", { type: "button", class: "ck-btn ck-btn--primary" }, [
    optOut ? t.us.acknowledge : t.banner.acceptAll,
  ]);
  const decline = h("button", { type: "button", class: "ck-btn ck-btn--secondary" }, [
    optOut ? t.us.optOut : t.banner.rejectAll,
  ]);
  const customize = h("button", { type: "button", class: "ck-btn ck-btn--ghost" }, [
    t.banner.customize,
  ]);

  on(affirm, "click", callbacks.onAffirm);
  on(decline, "click", callbacks.onDecline);
  on(customize, "click", callbacks.onCustomize);

  const inner = h("div", { class: "ck-inner" }, [
    h("h2", { class: "ck-title", id: "ck-banner-title" }, [optOut ? t.us.title : t.banner.title]),
    h("p", { class: "ck-body", id: "ck-banner-body" }, [bodyText(options)]),
    // Decline first in DOM order so a screen reader and the keyboard reach it at least as
    // early as the affirmative — "equally easy" covers reading order, not just pixels.
    h("div", { class: "ck-actions" }, [decline, affirm, customize]),
    createLinks(config, t),
  ]);

  panel.appendChild(inner);
  return panel;
}

function bodyText(options: BannerOptions): string {
  const { config, t, regime, gpc, hasThirdCountryTransfer } = options;
  const parts = [regime === "opt-out" ? t.us.body : t.banner.body];

  // Only an EU disclosure: "third country" is a term of art about leaving the EEA, which
  // says nothing to a visitor whose own country is the destination.
  if (regime === "opt-in" && hasThirdCountryTransfer) parts.push(` ${t.prefs.usTransferNote}`);
  if (gpc) parts.push(` ${t.us.gpcHonoured}`);
  if (config.controller.name) parts.push(` ${t.prefs.provider}: ${config.controller.name}.`);

  return parts.join("");
}

function createLinks(config: ConsentKitConfig, t: Dictionary): HTMLElement | null {
  const links: HTMLElement[] = [];
  if (config.controller.privacyUrl) {
    links.push(
      h("a", { class: "ck-link", href: config.controller.privacyUrl }, [t.banner.privacy]),
    );
  }
  if (config.controller.imprintUrl) {
    links.push(
      h("a", { class: "ck-link", href: config.controller.imprintUrl }, [t.banner.imprint]),
    );
  }
  return links.length > 0 ? h("div", { class: "ck-links" }, links) : null;
}

function panelClasses(config: ConsentKitConfig): string {
  const classes = ["ck-panel", `ck-panel--${config.layout}`];
  // The centered layout owns its own transform, so a position class would fight it.
  if (config.layout !== "center") classes.push(`ck-pos-${config.position}`);
  if (config.compact) classes.push("ck-panel--compact");
  return classes.join(" ");
}
